import { type FC, type ReactNode, useEffect, useState } from "react";

import {
  listIssueIntegrationProvidersAsUser,
  listSourceControlProvidersAsUser,
} from "../../dashboard-client";

interface ConnectedIntegration {
  borderColor: string;
  glowColor: string;
  icon: ReactNode;
  key: string;
  label: string;
}

const GitHubSvg: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 98 96"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M41.4395 69.3848C28.8066 67.8535 19.9062 58.7617 19.9062 46.9902C19.9062 42.2051 21.6289 37.0371 24.5 33.5918C23.2559 30.4336 23.4473 23.7344 24.8828 20.959C28.7109 20.4805 33.8789 22.4902 36.9414 25.2656C40.5781 24.1172 44.4062 23.543 49.0957 23.543C53.7852 23.543 57.6133 24.1172 61.0586 25.1699C64.0254 22.4902 69.2891 20.4805 73.1172 20.959C74.457 23.543 74.6484 30.2422 73.4043 33.4961C76.4668 37.1328 78.0937 42.0137 78.0937 46.9902C78.0937 58.7617 69.1934 67.6621 56.3691 69.2891C59.623 71.3945 61.8242 75.9883 61.8242 81.252L61.8242 91.2051C61.8242 94.0762 64.2168 95.7031 67.0879 94.5547C84.4102 87.9512 98 70.6289 98 49.1914C98 22.1074 75.9883 0 48.9043 0C21.8203 0 0 22.1074 0 49.1914C0 70.4375 13.4941 88.0469 31.6777 94.6504C34.2617 95.6074 36.75 93.8848 36.75 91.3008L36.75 83.6445C35.4102 84.2188 33.6875 84.6016 32.1562 84.6016C25.8398 84.6016 22.1074 81.1563 19.4277 74.7441C18.375 72.1602 17.2266 70.6289 15.0254 70.3418C13.877 70.2461 13.4941 69.7676 13.4941 69.1934C13.4941 68.0449 15.4082 67.1836 17.3223 67.1836C20.0977 67.1836 22.4902 68.9063 24.9785 72.4473C26.8926 75.2227 28.9023 76.4668 31.2949 76.4668C33.6875 76.4668 35.2187 75.6055 37.4199 73.4043C39.0469 71.7773 40.291 70.3418 41.4395 69.3848Z" />
  </svg>
);

const JiraSvg: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 256 256"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        id="emblem-jira-grad"
        x1="99.7%"
        y1="15.8%"
        x2="39.8%"
        y2="97.4%"
      >
        <stop stopColor="#0052CC" offset="0%" />
        <stop stopColor="#2684FF" offset="92.3%" />
      </linearGradient>
    </defs>
    <path
      d="M76 118c-4-4-10-4-13 1L1 245a7 7 0 0 0 6 10h88c3 0 5-1 6-4 19-39 8-98-25-133Z"
      fill="url(#emblem-jira-grad)"
    />
    <path
      d="M122 4c-35 56-33 117-10 163l42 84c1 3 4 4 7 4h87a7 7 0 0 0 7-10L134 4c-2-5-9-5-12 0Z"
      fill="#2684FF"
    />
  </svg>
);

function resolveIntegrations(
  issueProviders: readonly { is_enabled: boolean; type: string }[],
  scmProviders: readonly { is_enabled: boolean; type: string }[],
): ConnectedIntegration[] {
  const integrations: ConnectedIntegration[] = [];

  for (const p of scmProviders) {
    if (p.type === "github" && p.is_enabled) {
      integrations.push({
        borderColor:
          "border-slate-500/60 hover:border-slate-400 dark:border-slate-500/60 dark:hover:border-slate-300",
        glowColor: "shadow-[0_0_12px_rgba(148,163,184,0.35)]",
        icon: <GitHubSvg className="h-4 w-4 text-white" />,
        key: "github",
        label: "GitHub",
      });
    }
  }

  for (const p of issueProviders) {
    if (p.type === "jira" && p.is_enabled) {
      integrations.push({
        borderColor:
          "border-blue-500/50 hover:border-blue-400 dark:border-blue-500/50 dark:hover:border-blue-400",
        glowColor: "shadow-[0_0_12px_rgba(38,132,255,0.35)]",
        icon: <JiraSvg className="h-4 w-4" />,
        key: "jira",
        label: "Jira",
      });
    }
  }

  return integrations;
}

export const IntegrationEmblems: FC = () => {
  const [integrations, setIntegrations] = useState<ConnectedIntegration[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [issueResult, scmResult] = await Promise.all([
          listIssueIntegrationProvidersAsUser(),
          listSourceControlProvidersAsUser(),
        ]);

        if (!cancelled) {
          setIntegrations(
            resolveIntegrations(
              issueResult.data.providers,
              scmResult.data.providers,
            ),
          );
        }
      } catch {
        /* Silently degrade — emblems are decorative */
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (integrations.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5">
      {integrations.map((integration) => (
        <div
          key={integration.key}
          title={integration.label}
          className={`group relative inline-grid size-8 place-items-center rounded-md border bg-slate-100 transition-all duration-200 dark:bg-arti-background ${integration.borderColor} ${integration.glowColor} hover:scale-105`}
        >
          {integration.icon}
          <span className="pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-slate-300 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-slate-800">
            {integration.label}
          </span>
        </div>
      ))}
    </div>
  );
};
